import { useEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';
import Logger from 'utils/Logger';
import { useDebugModeStore } from '~/stores/debug';

/**
 * Debug component that logs React Router navigation events to the console.
 * Add this component inside your RouterProvider to track all navigations.
 *
 * Usage: <NavigationTracker /> anywhere inside your router context
 */
export const NavigationTracker = () => {
  const location = useLocation();
  const navigationType = useNavigationType();
  const { debugMode } = useDebugModeStore();
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (!debugMode) {
      return;
    }

    const timestamp = new Date().toISOString().slice(11, 23);

    if (isFirstRender.current) {
      Logger.group(`🧭 [${timestamp}] Initial navigation`);
      isFirstRender.current = false;
    } else {
      const typeEmoji =
        navigationType === 'PUSH'
          ? '➡️'
          : navigationType === 'POP'
            ? '⬅️'
            : '🔄';
      Logger.group(
        `🧭 [${timestamp}] Navigation (${typeEmoji} ${navigationType})`,
      );
    }

    Logger.log('Path:', location.pathname);
    Logger.log('Search:', location.search || '(none)');
    Logger.log('Hash:', location.hash || '(none)');
    Logger.log('Key:', location.key);

    if (location.state) {
      Logger.log('State:', location.state);
    }

    Logger.groupEnd();
  }, [location, navigationType, debugMode]);

  return null;
};
